import type { NextPage, GetStaticProps } from "next";
import { useTranslation } from "next-i18next/pages";
import SubpageShell from "components/common/SubpageShell";
import {
  CMS_REVALIDATE_SECONDS,
  serverSideCmsTranslations
} from "lib/cms/translations";
import styles from "../../styles/privacy/Privacy.module.css";

interface PrivacyInfo {
  title: string;
  paragraphs: string[];
  GDPRLinkText: string;
  GDPRLinkUrl: string;
  controllerPersonalData: {
    title: string;
    bullets: string[];
    email: string;
  };
  GDPR: {
    title: string;
    paragraphs: string[];
    bulletsTitle: string;
    bullets: string[];
    dataProtectionTitle: string;
    dataProtectionBullets: string[];
  };
  logFiles: {
    title: string;
    paragraphs: string[];
  };
  cookiesWebBeacons: {
    title: string;
    paragraphs: string[];
    cookiesLinkText: string;
    cookiesLinkUrl: string;
  };
  privacyPolicies: {
    title: string;
    paragraphs: string[];
  };
  contactForm: {
    title: string;
    paragraphs: string[];
    mailjetLinkText: string;
    mailjetLinkUrl: string;
  };
  intakeForm: {
    title: string;
    paragraphs: string[];
    mailjetLinkText: string;
    mailjetLinkUrl: string;
  };
  whitepaperDownload: {
    title: string;
    paragraphs: string[];
    mailjetLinkText: string;
    mailjetLinkUrl: string;
  };
  chatbot: {
    title: string;
    paragraphs: string[];
    mistralLinkText: string;
    mistralLinkUrl: string;
  };
  thirdPartyPP: {
    title: string;
    paragraphs: string[];
  };
  childrenInformation: {
    title: string;
    paragraphs: string[];
  };
  onlinePP: {
    title: string;
    paragraphs: string[];
  };
  consent: {
    title: string;
    paragraphs: string[];
  };
}

export const getStaticProps: GetStaticProps = async ({ locale }: any) => {
  return {
    props: {
      ...(await serverSideCmsTranslations(locale, ["common", "privacy"]))
    },
    revalidate: CMS_REVALIDATE_SECONDS
  };
};

const Privacy: NextPage = () => {
  const { t } = useTranslation("privacy");
  const privacy: PrivacyInfo = t("privacy", { returnObjects: true }) as PrivacyInfo;

  return (
    <SubpageShell
      title={t("global.websiteTitle")}
      description={t("global.websiteDescription")}
      heading={t("privacy.title")}
      subtitle=""
      path="/datenschutz"
    >
      <div className={styles.privacyContainer}>
        <h1 className={styles.title}>{t("privacy.title")}</h1>

        {privacy.paragraphs.map((el: string, index: number) => {
          if (index === privacy.paragraphs.length - 1) {
            return (
              <p key={index} className={styles.paragraph}>
                {el}{" "}
                <a
                  href={t("privacy.GDPRLinkUrl")}
                  className={styles.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("privacy.GDPRLinkText")}
                </a>
              </p>
            );
          } else {
            return (
              <p key={index} className={styles.paragraph}>
                {el}
              </p>
            );
          }
        })}

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>
            {t("privacy.controllerPersonalData.title")}
          </div>

          <ul className={styles.bullets}>
            {privacy.controllerPersonalData.bullets.map(
              (bullet: string, index: number) => {
                if (
                  index ===
                  privacy.controllerPersonalData.bullets.length - 1
                ) {
                  return (
                    <li key={index} className={styles.bullet}>
                      {bullet}

                      <a
                        href={`mailto:${t("privacy.controllerPersonalData.email")}`}
                        className={styles.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("privacy.controllerPersonalData.email")}
                      </a>
                    </li>
                  );
                } else {
                  return (
                    <li key={index} className={styles.paragraph}>
                      {bullet}
                    </li>
                  );
                }
              }
            )}
          </ul>
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>{t("privacy.GDPR.title")}</div>

          {privacy.GDPR.paragraphs.map((el: string, index: number) => (
            <p key={index} className={styles.paragraph}>
              {el}
            </p>
          ))}

          <div className={styles.bulletsTitle}>
            {t("privacy.GDPR.bulletsTitle")}
          </div>

          <ul className={styles.bullets}>
            {privacy.GDPR.bullets.map((bullet: string, index: number) => (
              <li key={index} className={styles.bullet}>
                {bullet}
              </li>
            ))}
          </ul>

          <div className={styles.bulletsTitle}>
            {t("privacy.GDPR.dataProtectionTitle")}
          </div>

          <ul className={styles.bullets}>
            {privacy.GDPR.dataProtectionBullets.map(
              (bullet: string, index: number) => (
                <li key={index} className={styles.bullet}>
                  {bullet}
                </li>
              )
            )}
          </ul>
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>{t("privacy.logFiles.title")}</div>

          {privacy.logFiles.paragraphs.map((el: string, index: number) => (
            <p key={index} className={styles.paragraph}>
              {el}
            </p>
          ))}
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>
            {t("privacy.cookiesWebBeacons.title")}
          </div>

          {privacy.cookiesWebBeacons.paragraphs.map(
            (el: string, index: number) => {
              if (index === privacy.cookiesWebBeacons.paragraphs.length - 1) {
                return (
                  <p key={index} className={styles.paragraph}>
                    {el}{" "}
                    <a
                      href={t("privacy.cookiesWebBeacons.cookiesLinkUrl")}
                      className={styles.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("privacy.cookiesWebBeacons.cookiesLinkText")}
                    </a>
                  </p>
                );
              } else {
                return (
                  <p key={index} className={styles.paragraph}>
                    {el}
                  </p>
                );
              }
            }
          )}
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>
            {t("privacy.privacyPolicies.title")}
          </div>

          {privacy.privacyPolicies.paragraphs.map(
            (el: string, index: number) => (
              <p key={index} className={styles.paragraph}>
                {el}
              </p>
            )
          )}
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>
            {t("privacy.contactForm.title")}
          </div>

          {privacy.contactForm.paragraphs.map((el: string, index: number) => {
            if (index === privacy.contactForm.paragraphs.length - 1) {
              return (
                <p key={index} className={styles.paragraph}>
                  {el}{" "}
                  <a
                    href={t("privacy.contactForm.mailjetLinkUrl")}
                    className={styles.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("privacy.contactForm.mailjetLinkText")}
                  </a>
                </p>
              );
            }

            return (
              <p key={index} className={styles.paragraph}>
                {el}
              </p>
            );
          })}
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>
            {t("privacy.intakeForm.title")}
          </div>

          {privacy.intakeForm.paragraphs.map((el: string, index: number) => {
            if (index === privacy.intakeForm.paragraphs.length - 1) {
              return (
                <p key={index} className={styles.paragraph}>
                  {el}{" "}
                  <a
                    href={t("privacy.intakeForm.mailjetLinkUrl")}
                    className={styles.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("privacy.intakeForm.mailjetLinkText")}
                  </a>
                </p>
              );
            } else {
              return (
                <p key={index} className={styles.paragraph}>
                  {el}
                </p>
              );
            }
          })}
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>
            {t("privacy.whitepaperDownload.title")}
          </div>

          {privacy.whitepaperDownload.paragraphs.map(
            (el: string, index: number) => {
              if (index === privacy.whitepaperDownload.paragraphs.length - 1) {
                return (
                  <p key={index} className={styles.paragraph}>
                    {el}{" "}
                    <a
                      href={t("privacy.whitepaperDownload.mailjetLinkUrl")}
                      className={styles.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("privacy.whitepaperDownload.mailjetLinkText")}
                    </a>
                  </p>
                );
              } else {
                return (
                  <p key={index} className={styles.paragraph}>
                    {el}
                  </p>
                );
              }
            }
          )}
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>{t("privacy.chatbot.title")}</div>

          {privacy.chatbot.paragraphs.map((el: string, index: number) => {
            if (index === privacy.chatbot.paragraphs.length - 1) {
              return (
                <p key={index} className={styles.paragraph}>
                  {el}{" "}
                  <a
                    href={t("privacy.chatbot.mistralLinkUrl")}
                    className={styles.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("privacy.chatbot.mistralLinkText")}
                  </a>
                </p>
              );
            } else {
              return (
                <p key={index} className={styles.paragraph}>
                  {el}
                </p>
              );
            }
          })}
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>
            {t("privacy.thirdPartyPP.title")}
          </div>

          {privacy.thirdPartyPP.paragraphs.map((el: string, index: number) => (
            <p key={index} className={styles.paragraph}>
              {el}
            </p>
          ))}
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>
            {t("privacy.childrenInformation.title")}
          </div>

          {privacy.childrenInformation.paragraphs.map(
            (el: string, index: number) => (
              <p key={index} className={styles.paragraph}>
                {el}
              </p>
            )
          )}
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>{t("privacy.onlinePP.title")}</div>

          {privacy.onlinePP.paragraphs.map((el: string, index: number) => (
            <p key={index} className={styles.paragraph}>
              {el}
            </p>
          ))}
        </section>

        <section className={styles.sectionContainer}>
          <div className={styles.subTitle}>{t("privacy.consent.title")}</div>

          {privacy.consent.paragraphs.map((el: string, index: number) => (
            <p key={index} className={styles.paragraph}>
              {el}
            </p>
          ))}
        </section>
      </div>
    </SubpageShell>
  );
};

export default Privacy;
